import type { Status, ServiceMap } from "../../types";
import type { Edge } from "@xyflow/react";
import { alertService } from "./alertsService";
import {
  applyDependencyScoreImpact,
  computeServiceFromAlertStatuses,
} from "./serviceScoringService";
import { serviceMapS3Store } from "./internal/serviceMapS3Store";

const writeMap = (id: string, value: ServiceMap) => {
  return serviceMapS3Store.put(id, value);
};

const buildRelationships = (edges: ServiceMap["edges"]) => {
  const dependsOn = new Map<string, Set<string>>();
  const dependents = new Map<string, Set<string>>();

  for (const e of edges as Edge[]) {
    (
      dependents.get(e.source) ??
      dependents.set(e.source, new Set()).get(e.source)!
    ).add(e.target);
    (
      dependsOn.get(e.target) ??
      dependsOn.set(e.target, new Set()).get(e.target)!
    ).add(e.source);
  }

  const toArrayMap = (m: Map<string, Set<string>>) => {
    const out = new Map<string, string[]>();
    for (const [k, v] of m) {out.set(k, Array.from(v));}
    return out;
  };

  return {
    dependsOnById: toArrayMap(dependsOn),
    dependentsById: toArrayMap(dependents),
  };
};

const computeAndApplyStatuses = async (
  serviceMapId: string,
  sourceMap?: ServiceMap,
) => {
  const systemMap = sourceMap ?? await get(serviceMapId);
  const relations = buildRelationships(systemMap.edges);
  const dependsOnById: Map<string, string[]> = relations.dependsOnById;
  const dependentsById: Map<string, string[]> = relations.dependentsById;
  const computedDataByNodeId = new Map<
    string,
    ServiceMap["nodes"][number]["data"]
  >();
  const finalDataByNodeId = new Map<string, ServiceMap["nodes"][number]["data"]>();
  const alertIds = new Set<string>();

  for (const system of systemMap.nodes) {
    const kpis = system.data.kpis ?? [];
    for (const kpi of kpis) {
      for (const alertId of kpi.alerts ?? []) {
        alertIds.add(alertId);
      }
    }
  }

  const alertStatusById = new Map<string, Status>();
  await Promise.all(
    Array.from(alertIds).map(async (alertId) => {
      const status = await alertService.getStatus(alertId);
      alertStatusById.set(alertId, status);
    }),
  );

  for (const system of systemMap.nodes) {
    const computedData = computeServiceFromAlertStatuses(system.data, alertStatusById);
    computedDataByNodeId.set(system.id, computedData);
  }

  const nodeIds = new Set(systemMap.nodes.map((node) => node.id));
  const indegreeByNodeId = new Map<string, number>();
  for (const nodeId of nodeIds) {
    const dependencyCount = (dependsOnById.get(nodeId) ?? []).filter((depId) =>
      nodeIds.has(depId),
    ).length;
    indegreeByNodeId.set(nodeId, dependencyCount);
  }

  const queue = Array.from(indegreeByNodeId.entries())
    .filter(([, indegree]) => indegree === 0)
    .map(([nodeId]) => nodeId);

  while (queue.length > 0) {
    const nodeId = queue.shift();
    if (!nodeId) {
      continue;
    }

    const baseData = computedDataByNodeId.get(nodeId);
    if (!baseData) {
      continue;
    }

    const dependencyScores = (dependsOnById.get(nodeId) ?? [])
      .filter((depId) => nodeIds.has(depId))
      .map((depId) => {
        return (
          finalDataByNodeId.get(depId)?.metrics?.score ??
          computedDataByNodeId.get(depId)?.metrics?.score ??
          100
        );
      });

    finalDataByNodeId.set(
      nodeId,
      applyDependencyScoreImpact(baseData, dependencyScores),
    );

    for (const dependentId of dependentsById.get(nodeId) ?? []) {
      if (!nodeIds.has(dependentId)) {
        continue;
      }
      const remaining = (indegreeByNodeId.get(dependentId) ?? 0) - 1;
      indegreeByNodeId.set(dependentId, remaining);
      if (remaining === 0) {
        queue.push(dependentId);
      }
    }
  }

  // Cycles are resolved with a deterministic single pass using available scores.
  for (const system of systemMap.nodes) {
    if (finalDataByNodeId.has(system.id)) {
      continue;
    }
    const baseData = computedDataByNodeId.get(system.id) ?? system.data;
    const dependencyScores = (dependsOnById.get(system.id) ?? [])
      .filter((depId) => nodeIds.has(depId))
      .map((depId) => {
        return (
          finalDataByNodeId.get(depId)?.metrics?.score ??
          computedDataByNodeId.get(depId)?.metrics?.score ??
          100
        );
      });
    finalDataByNodeId.set(
      system.id,
      applyDependencyScoreImpact(baseData, dependencyScores),
    );
  }

  for (const system of systemMap.nodes) {
    system.data = finalDataByNodeId.get(system.id) ?? computedDataByNodeId.get(system.id) ?? system.data;
  }
  return systemMap;
};

const get = async (id: string) => {
  return serviceMapS3Store.get(id);
};

export const serviceMapService = {
  get,
  getWithComputedStatuses: async (serviceMapId: string) => {
    const systemMap = await get(serviceMapId);
    if (!systemMap || systemMap.nodes.length === 0) {
      return systemMap;
    }
    const computedMap = await computeAndApplyStatuses(serviceMapId, systemMap);
    return computedMap;
  },
  create: async (id: string): Promise<void> => {
    await writeMap(id, { nodes: [], edges: [] });
  },
  updateServiceStatuses: async (serviceMapId: string) => {
    const systemMap = await get(serviceMapId);
    if (!systemMap || systemMap.nodes.length === 0) {
      return;
    }
    const computedMap = await computeAndApplyStatuses(serviceMapId, systemMap);
    if (computedMap.nodes.length === 0) {
      return;
    }
    await writeMap(serviceMapId, computedMap);
  },
  getRelationships: async (serviceMapId: string) => {
    const { edges } = await get(serviceMapId);
    return buildRelationships(edges);
  },
  patch: async (id: string, patch: ServiceMap): Promise<string> => {
    if (!patch || !patch.nodes || !patch.edges) {
      throw new Error("Invalid service map structure");
    }
    await writeMap(id, patch);
    return id;
  },
};
